import axiosInstance from "../../../interceptors/token";
import imageCompression from "browser-image-compression";
import { API_BASE_URL } from "../../../constants";
import { i18n } from "@lingui/core";
import { calculateTotalWorkHours } from "./projectDetailTotalWorkHoursService";
import { materialenForSubmit, withKeys } from "../MaterialenLijst";

/**
 * Handles submitting the delivery note.
 */
export const _handleSubmit = async (
  e,
  selectedCopyId,
  files,
  formData,
  selectedDate,
  id,
  projectOrder,
  setLoading,
  setUserWorkEntry,
  setIsEditing,
  setFileError,
  setProjectFinishedError,
  uploadProgress,
  setUploadProgress
) => {
  e.preventDefault();

  if (selectedCopyId) {
    try {
      await axiosInstance.post(`${API_BASE_URL}werk/${selectedCopyId}/copy/`);
      window.location.reload();
    } catch (error) {
      console.error("Error copying work entry:", error);
    } finally {
      setLoading(false);
      return;
    }
  }

  if (files.length === 0) {
    setFileError(true);
    return;
  }

  if (formData.finished === undefined) {
    setProjectFinishedError(true);
    return;
  }

  // Controleren of de starttijd voor de eindtijd ligt
  const startDate = new Date(
    `1970-01-01T${formData.start_time.slice(0, 5)}:00`
  );
  const endDate = new Date(`1970-01-01T${formData.end_time.slice(0, 5)}:00`);

  if (startDate >= endDate) {
    alert(i18n._("Work start time must be before work end time."));
    return;
  }

  // Melding geven aan werknemer als hij geen pauze aanduid terwijl zen werkuren over de middag vallen
  const beforeNoon = new Date("1970-01-01T12:00:00");
  const afterOnePM = new Date("1970-01-01T13:00:00");

  if (
    startDate < beforeNoon &&
    endDate > afterOnePM &&
    (formData.break === "0.0" || formData.break === 0)
  ) {
    const confirmed = window.confirm(
      i18n._("Are you sure you didn't take a break around midday?")
    );
    if (!confirmed) return;
  }

  if (
    projectOrder.indexOf(parseInt(id)) === 0 &&
    (formData.preparation === "0.0" || formData.preparation === 0)
  ) {
    const confirmed = window.confirm(
      i18n._(
        "Are you sure you didn't do any preparation work in the warehouse in the morning?"
      )
    );
    if (!confirmed) return;
  }

  if (projectOrder.indexOf(parseInt(id)) === projectOrder.length - 1) {
    const totalWorkHours = await calculateTotalWorkHours(
      id,
      selectedDate,
      formData,
      startDate,
      endDate
    );
    if (totalWorkHours < 8) {
      const confirmed = window.confirm(
        i18n._(
          "Your total hours for today add up to less than 8 hours. Are you sure you filled in all your projects correctly?"
        )
      );
      if (!confirmed) return;
    }
  }

  setLoading(true);

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  const timingData = {
    date: selectedDate,
    start_time: formData.start_time,
    end_time: formData.end_time,
  };

  const submissionData = {
    preparation_hours: formData.preparation,
    total_break: formData.break,
    after_magazine_hours: formData.warehouse,
    transport_time: formData.transport,
    materials: formData.materials,
    // 27/10/2026 (Bartel): gestructureerde materialen; de backend vervangt de bestaande lijst.
    materialen: materialenForSubmit(formData.materialen),
    description_work: formData.description_work,
    finished: formData.finished,
    photos: [],
    date: selectedDate,
  };

  let timingResponse = undefined;
  let response = undefined;
  try {
    timingResponse = await axiosInstance.post(
      `${API_BASE_URL}projects/${id}/werk/timing/`,
      timingData
    );
    response = await axiosInstance.post(
      `${API_BASE_URL}projects/${id}/werk/`,
      submissionData
    );
  } catch (error) {
    console.error("Error submitting delivery note or timing data:", error);
  }

  const uploadSinglePhoto = async (photo) => {
    const options = {
      maxSizeMB: 0.1,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };

    if (!(photo instanceof File)) return;

    let compressedFile = undefined;
    try {
      const compressedBlob = await imageCompression(photo, options);
      compressedFile = new File([compressedBlob], photo.name, {
        type: photo.type,
      });
    } catch (error) {
      console.log("File " + photo.name + " is not a compressable file");
      compressedFile = photo;
    }

    try {
      const linkResponse = await axiosInstance.post(
          `${API_BASE_URL}projects/${id}/dropbox/worker/upload_link/`,
          {
            date: selectedDate,
            filename: compressedFile.name,
          }
      );

      const { upload_link } = linkResponse.data;

      const dropboxResponse = await fetch(upload_link, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
        },
        body: compressedFile,
      });

      if (!dropboxResponse.ok) {
        throw new Error(`Dropbox upload failed: ${dropboxResponse.statusText}`);
      }

      console.log("Upload successful for:", compressedFile.name);

    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };
  const uploadAllPhotos = async () => {
    const validFiles = files.filter((item) => item instanceof File);
    const total = validFiles.length;

    setUploadProgress(0);

    let completed = 0;

    const uploadPromises = validFiles.map((photo) =>
      uploadSinglePhoto(photo).then(() => {
        completed += 1;
        const percent = Math.round((completed / total) * 100);
        setUploadProgress(percent);
      })
    );

    await Promise.all(uploadPromises);
  };

  await uploadAllPhotos();
  setUserWorkEntry({ ...timingResponse.data, ...response.data });
  setLoading(false);
  setIsEditing(false);
  setUploadProgress(0);
};

/**
 * Handles deleting the delivery note.
 */
export const _handleDelete = async (
  id,
  selectedDate,
  userWorkEntry,
  setUserWorkEntry,
  setFiles,
  setFormData
) => {
  const confirmDelete = window.confirm(
    i18n._("Are you sure you want to delete this delivery note?")
  );
  if (!confirmDelete) return;

  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });

  try {
    await axiosInstance.delete(
      `${API_BASE_URL}projects/${id}/werk/${userWorkEntry.id}/`
    );
    setUserWorkEntry(null);
    setFiles([]);

    let timingData = null;
    try {
      const timingResponse = await axiosInstance.get(
        `${API_BASE_URL}projects/${id}/werk/timing/`,
        { params: { date: selectedDate } }
      );
      timingData = timingResponse.data;
    } catch (error) {
      if (error.response?.status === 404) {
        console.log("No timing data found after delivery note deletion.");
      } else {
        console.error("Error fetching timing data after deletion:", error);
      }
    }

    setFormData({
      preparation: "0.0",
      start_time: timingData?.start_time || "",
      break: "0.0",
      end_time: timingData?.end_time || "",
      warehouse: "0.0",
      transport: "0.0",
      materials: "",
      materialen: [],
      finished: undefined,
      description_work: "",
    });

    //await axiosInstance.delete(`${API_BASE_URL}projects/${id}/dropbox/`, { params: {date: selectedDate}});
  } catch (error) {
    console.error("Error deleting delivery note:", error);
  }
};

/**
 * Handles editing the delivery note.
 */
export const _handleEdit = (userWorkEntry, setFormData, setIsEditing) => {
  console.log(userWorkEntry);

  setFormData({
    preparation: userWorkEntry.preparation_hours,
    start_time: userWorkEntry.start_time,
    break: userWorkEntry.total_break,
    end_time: userWorkEntry.end_time,
    warehouse: userWorkEntry.after_magazine_hours,
    transport: userWorkEntry.transport_time,
    materials: userWorkEntry.materials,
    materialen: withKeys(userWorkEntry.materialen),
    finished: userWorkEntry.finished,
    description_work: userWorkEntry.description_work,
  });

  setIsEditing(true);
};
